import {
  DataGridPremium,
  GridColDef,
  GridRowsProp,
} from '@mui/x-data-grid-premium'
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
  Tabs,
  Tab,
  Tooltip,
  Card,
  TextField,
} from '@mui/material'
import {
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import {
  fetchElevatedUsers,
  selectElevatedUsersByService,
  selectElevatedUsersStatusByService,
} from '../../manage.slice'
import { MIN_BUTTON_SIZE, ROSTER_URLS } from '../../constants'

interface LightweightUser {
  id: string
  firstName: string
  lastName: string
  primaryEmail: string
}

interface UserDataGridProps {
  users: LightweightUser[]
  isLoading: boolean
  linkedServiceId: string
  searchQuery: string
  onSearchChange: (value: string) => void
  onUserAction?: (action: 'view', userId: string) => void
}

export default function UserDataGrid({
  users,
  isLoading,
  linkedServiceId,
  searchQuery,
  onSearchChange,
}: UserDataGridProps) {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let [activeTab, setActiveTab] = useState(0)

  let elevatedUsers = useAppSelector(
    selectElevatedUsersByService(linkedServiceId),
  )
  let elevatedUsersStatus = useAppSelector(
    selectElevatedUsersStatusByService(linkedServiceId),
  )

  useEffect(() => {
    if (linkedServiceId && elevatedUsersStatus === 'uninitialized') {
      dispatch(fetchElevatedUsers(linkedServiceId))
    }
  }, [linkedServiceId, elevatedUsersStatus, dispatch])

  let allUsersCount = users.length
  let creatorsCount = useMemo(
    () =>
      users.filter((user) => elevatedUsers.creators.includes(user.id)).length,
    [users, elevatedUsers.creators],
  )
  let managersCount = useMemo(
    () =>
      users.filter((user) => elevatedUsers.managers.includes(user.id)).length,
    [users, elevatedUsers.managers],
  )

  let filteredUsers = useMemo(() => {
    if (activeTab === 0) {
      return users // All Users
    }
    if (activeTab === 1) {
      return users.filter((user) => elevatedUsers.creators.includes(user.id)) // Creators
    }
    if (activeTab === 2) {
      return users.filter((user) => elevatedUsers.managers.includes(user.id)) // Managers
    }
    return users
  }, [activeTab, users, elevatedUsers.creators, elevatedUsers.managers])

  let handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  let a11yProps = (index: number) => ({
    id: `user-role-tab-${index}`,
    'aria-controls': `user-role-tabpanel-${index}`,
  })

  let UserToolbar = useCallback(
    () => <DataGridToolbar fileName="roster-users" />,
    [],
  )

  let columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value, row) =>
        `${row?.firstName || ''} ${row?.lastName || ''}`.trim(),
      renderCell: (params) => (
        <Box className="flex items-center h-full">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'medium',
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'primaryEmail',
      headerName: 'Email',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        let user = params.row
        let fullName =
          `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          user.primaryEmail
        return (
          <Tooltip title="View details">
            <IconButton
              onClick={() => {
                navigate(
                  `${ROSTER_URLS.INDEX}/${linkedServiceId}/users/${params.id as string}`,
                )
              }}
              aria-label={`View ${fullName}`}
              sx={{ minWidth: MIN_BUTTON_SIZE, minHeight: MIN_BUTTON_SIZE }}
            >
              <ViewIcon color="primary" />
            </IconButton>
          </Tooltip>
        )
      },
    },
  ]

  let rows: GridRowsProp = (filteredUsers || [])
    .filter((user) => user && user.id) // Filter out invalid users
    .map((user) => ({
      id: user.id,
      firstName: user.firstName || '-',
      lastName: user.lastName || '-',
      primaryEmail: user.primaryEmail || '-',
    }))

  return (
    <Card sx={{ bgcolor: 'background.paper' }}>
      <Box className="px-6 pt-6">
        <Typography variant="h6" className="mb-4">
          Users
        </Typography>
        <Typography variant="body2" color="text.secondary" className="mb-6">
          Search by email address for users that have not signed in to their
          account yet as their name is added on their intial login.
        </Typography>

        <TextField
          fullWidth
          sx={{ backgroundColor: 'background.paper' }}
          label="Search Users"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <SearchIcon className="mr-2" sx={{ color: 'text.secondary' }} />
              ),
            },
          }}
          disabled={!linkedServiceId}
          helperText={
            !linkedServiceId ? 'Select a linked service first' : undefined
          }
        />
      </Box>

      <Box className="border-b" sx={{ borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="User role tabs"
        >
          <Tab label={`All Users (${allUsersCount})`} {...a11yProps(0)} />
          <Tab label={`Creators (${creatorsCount})`} {...a11yProps(1)} />
          <Tab label={`Managers (${managersCount})`} {...a11yProps(2)} />
        </Tabs>
      </Box>

      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        Showing {filteredUsers.length}{' '}
        {activeTab === 0 ? 'users' : activeTab === 1 ? 'creators' : 'managers'}
      </Box>

      <Box
        role="tabpanel"
        id={`user-role-tabpanel-${activeTab}`}
        aria-labelledby={`user-role-tab-${activeTab}`}
        className="w-full"
        sx={{ height: 600 }}
      >
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            height={600}
            sx={{ borderRadius: 1 }}
            aria-label="Loading user data"
          />
        ) : (
          <DataGridPremium
            rows={rows}
            columns={columns}
            slots={{ toolbar: UserToolbar }}
            showToolbar
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
              pinnedColumns: { right: ['actions'] },
            }}
            disableRowSelectionOnClick
            sx={{
              border: 1,
              borderRadius: 1,
              borderColor: 'divider',
              '& .MuiDataGrid-main': {
                bgcolor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'background.paperDark',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-footerContainer': {
                borderColor: 'divider',
                bgcolor: 'background.paperDark',
              },
            }}
          />
        )}
      </Box>
    </Card>
  )
}
